import {
  createElement,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Maximize2,
  Minimize2,
  RotateCcw,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { usePreview } from "./PreviewContext";
import { extensionOf, fileIconFor } from "../views/cardUtils";
import { importDocxToSfdt } from "../../api/documentEditorApi";

// Mounted on every page, so the editor (Syncfusion, several MB) is loaded
// only when a Word file is actually previewed.
const WordEditor = lazy(
  () => import("../editor/DocumentEditorContainerComponent"),
);

const noopSetPlaceholders = () => {};

type PreviewKind =
  "word" | "excel" | "pdf" | "image" | "video" | "audio" | "text" | "other";

/**
 * Exactly one viewer per file. The checks used to run independently, so a
 * .docx whose caller labelled it "application/pdf" (member profiles label
 * every document that way) matched both the PDF iframe and the Word viewer,
 * and the two rendered side by side, each at half width. The extension is
 * the more reliable signal and decides first; the MIME type only fills in
 * when there is no recognised extension.
 */
function previewKind(
  name: string | undefined,
  type: string | undefined,
): PreviewKind {
  const ext = name?.split(".").pop()?.toLowerCase() ?? "";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(ext))
    return "image";
  if (["mp4", "webm", "mov", "mkv"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "aac", "m4a"].includes(ext)) return "audio";
  if (["txt", "json", "log", "md"].includes(ext)) return "text";

  const t = type?.toLowerCase() ?? "";
  if (t.includes("wordprocessingml") || t.includes("msword")) return "word";
  if (t.includes("spreadsheetml") || t.includes("ms-excel")) return "excel";
  if (t.includes("pdf")) return "pdf";
  if (t.startsWith("image")) return "image";
  if (t.startsWith("video")) return "video";
  if (t.startsWith("audio")) return "audio";
  if (t.startsWith("text")) return "text";
  return "other";
}

function formatBytes(bytes?: number): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

const KIND_LABEL: Record<PreviewKind, string> = {
  word: "Word document",
  excel: "Spreadsheet",
  pdf: "PDF",
  image: "Image",
  video: "Video",
  audio: "Audio",
  text: "Text",
  other: "File",
};

// The viewer is a dark room in every theme, so the file is what stands out.
// Its chrome carries the `dark` class: the dark theme's tokens apply there,
// while the file itself (a Word page, a PDF) keeps its own colours.
const headerButton =
  "grid h-9 w-9 place-items-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:pointer-events-none disabled:opacity-35";

const navButton =
  "absolute top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60";

/** Icon colour per kind on the dark chrome, matching the gallery's tiles. */
const KIND_ICON: Record<PreviewKind, string> = {
  word: "text-sky-300",
  excel: "text-emerald-300",
  pdf: "text-rose-300",
  image: "text-emerald-300",
  video: "text-violet-300",
  audio: "text-indigo-300",
  text: "text-slate-300",
  other: "text-slate-300",
};

export function GlobalPreviewModal() {
  const { t } = useTranslation("fileGallery");
  const { state, closePreview, next, prev, setIndex } = usePreview();
  const file = state.files[state.index];
  const rootRef = useRef<HTMLDivElement>(null);
  const activeTileRef = useRef<HTMLButtonElement>(null);

  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const [docxSfdt, setDocxSfdt] = useState<unknown>(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxUnavailable, setDocxUnavailable] = useState(false);

  const kind = previewKind(file?.name, file?.type);
  const ext = file?.name?.includes(".")
    ? file.name.split(".").pop()?.toLowerCase()
    : undefined;
  const isWord = kind === "word";
  const isImage = kind === "image";
  const syncfusionConfigured = Boolean(
    import.meta.env.VITE_DOCXEDITOR_API_HOST,
  );
  const hasMany = state.files.length > 1;

  // Zoom and rotation belong to the file being looked at: moving to the next
  // file used to carry the previous one's 300% zoom and 90° turn with it.
  useEffect(() => {
    setZoom(1);
    setRotate(0);
  }, [state.index, file?.url]);

  // Arrow keys can move past what the strip shows; keep the open file's tile
  // in view.
  useEffect(() => {
    if (!state.open) return;
    activeTileRef.current?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [state.open, state.index]);

  // Tracks the browser's own full-screen state, so Esc or the browser's exit
  // gesture keep the button's icon and tooltip honest.
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement != null);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (!state.open && document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    }
  }, [state.open]);

  useEffect(() => {
    if (!state.open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.fullscreenElement) closePreview();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [state.open, next, prev, closePreview]);

  // Load the .docx into the Syncfusion editor for a real, read-only preview;
  // falls back to a download link if Syncfusion isn't configured or the
  // import fails at runtime.
  useEffect(() => {
    if (!state.open || !isWord || !syncfusionConfigured || !file) {
      setDocxSfdt(null);
      setDocxLoading(false);
      setDocxUnavailable(false);
      return;
    }

    let cancelled = false;
    setDocxLoading(true);
    setDocxUnavailable(false);
    setDocxSfdt(null);

    (async () => {
      try {
        const res = await fetch(file.url);
        if (!res.ok) throw new Error(`Could not read file (${res.status})`);
        const blob = await res.blob();
        const asFile = new File([blob], file.name, {
          type:
            blob.type ||
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        const sfdt = await importDocxToSfdt(asFile);
        if (!cancelled) setDocxSfdt(sfdt);
      } catch (err) {
        console.error(
          "Syncfusion preview unavailable, falling back to download link:",
          err,
        );
        if (!cancelled) setDocxUnavailable(true);
      } finally {
        if (!cancelled) setDocxLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.open, isWord, syncfusionConfigured, file?.url, file?.name]);

  if (!state.open || !file) return null;

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await rootRef.current?.requestFullscreen();
      }
    } catch {
      // Full screen can be refused (iframe policy, user setting); stay put.
    }
  };

  const downloadLink = (
    <a
      href={file.url}
      target="_blank"
      rel="noreferrer"
      download={file.name}
      className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-zinc-900 transition hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
    >
      <Download size={16} />
      {t("fileGallery.preview.downloadFile", { defaultValue: "Download file" })}
    </a>
  );

  const label = (key: string, fallback: string) =>
    t(`fileGallery.preview.${key}`, { defaultValue: fallback });

  const showSyncfusionPreview =
    isWord && syncfusionConfigured && !docxUnavailable;

  const docLoadingState = (
    <div className="w-full h-full flex items-center justify-center gap-2 text-[rgb(var(--text-secondary))] text-sm">
      <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
      {label("loading", "Loading document...")}
    </div>
  );

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      className="fixed inset-0 z-[9999] flex flex-col bg-zinc-950"
    >
      {/* HEADER */}
      <div className="dark flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-2.5 text-white">
        {/* What is being looked at: name, kind, size and position, so a
            reader flipping through several files knows where they are. */}
        <div className="flex min-w-0 max-w-[60%] items-center gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.08] ${KIND_ICON[kind]}`}
          >
            {createElement(fileIconFor(file), { size: 18 })}
          </span>
          <div className="min-w-0">
            <div
              className="truncate text-sm font-semibold text-white"
              title={file.name}
            >
              {file.name}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 text-xs text-white/55">
              <span>
                {t(`fileGallery.preview.kind.${kind}`, {
                  defaultValue: KIND_LABEL[kind],
                })}
                {ext ? ` · ${ext.toUpperCase()}` : ""}
              </span>
              {formatBytes(file.size) && (
                <span>· {formatBytes(file.size)}</span>
              )}
              {hasMany && (
                <span>
                  ·{" "}
                  {t("fileGallery.preview.position", {
                    defaultValue: "{{current}} of {{total}}",
                    current: state.index + 1,
                    total: state.files.length,
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-1">
          {/* Zoom and rotate act on images only; documents, PDFs and media
              players have their own zoom, so the buttons are not offered
              where they would do nothing. */}
          {isImage && (
            <div className="mr-1 flex items-center gap-0.5 rounded-xl bg-white/[0.06] p-0.5">
              <button
                type="button"
                className={headerButton}
                onClick={() => setZoom((z) => Math.min(5, z + 0.25))}
                disabled={zoom >= 5}
                title={label("zoomIn", "Zoom in")}
                aria-label={label("zoomIn", "Zoom in")}
              >
                <ZoomIn size={16} />
              </button>
              <button
                type="button"
                className={headerButton}
                onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
                disabled={zoom <= 0.25}
                title={label("zoomOut", "Zoom out")}
                aria-label={label("zoomOut", "Zoom out")}
              >
                <ZoomOut size={16} />
              </button>
              <span className="w-11 text-center text-xs font-semibold tabular-nums text-white/70">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                className={headerButton}
                onClick={() => setRotate((r) => (r + 90) % 360)}
                title={label("rotate", "Rotate 90°")}
                aria-label={label("rotate", "Rotate 90°")}
              >
                <RotateCw size={16} />
              </button>
              <button
                type="button"
                className={headerButton}
                onClick={() => {
                  setZoom(1);
                  setRotate(0);
                }}
                disabled={zoom === 1 && rotate === 0}
                title={label("reset", "Reset zoom and rotation")}
                aria-label={label("reset", "Reset zoom and rotation")}
              >
                <RotateCcw size={16} />
              </button>
            </div>
          )}

          <a
            href={file.url}
            target="_blank"
            rel="noreferrer"
            download={file.name}
            className={headerButton}
            title={label("download", "Download")}
            aria-label={label("download", "Download")}
          >
            <Download size={16} />
          </a>

          <button
            type="button"
            className={headerButton}
            onClick={toggleFullscreen}
            title={
              fullscreen
                ? label("exitFullscreen", "Exit full screen")
                : label("fullscreen", "Full screen")
            }
            aria-label={
              fullscreen
                ? label("exitFullscreen", "Exit full screen")
                : label("fullscreen", "Full screen")
            }
          >
            {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>

          <span aria-hidden className="mx-1 h-5 w-px bg-white/15" />
          <button
            type="button"
            className={headerButton}
            onClick={closePreview}
            title={label("close", "Close (Esc)")}
            aria-label={label("close", "Close (Esc)")}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-14 py-4 sm:px-20">
          {hasMany && (
            <button
              type="button"
              onClick={prev}
              title={label("previous", "Previous file (←)")}
              aria-label={label("previous", "Previous file (←)")}
              className={`${navButton} left-2 sm:left-4`}
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {kind === "image" && (
            <img
              src={file.url}
              alt={file.name}
              style={{
                transform: `scale(${zoom}) rotate(${rotate}deg)`,
                // A light checkerboard behind the image: a transparent logo
                // with dark lettering would otherwise vanish into the dark
                // viewer. Opaque photos cover it completely.
                backgroundColor: "#ffffff",
                backgroundImage:
                  "conic-gradient(#e5e7eb 25%, transparent 0 50%, #e5e7eb 0 75%, transparent 0)",
                backgroundSize: "16px 16px",
              }}
              className="max-h-full max-w-full rounded-lg object-contain shadow-2xl shadow-black/50 transition"
            />
          )}

          {kind === "video" && (
            <video
              src={file.url}
              controls
              playsInline
              preload="metadata"
              style={{ colorScheme: "dark" }}
              className="max-h-full max-w-full rounded-xl bg-black shadow-2xl shadow-black/50"
            />
          )}

          {kind === "pdf" && (
            <iframe
              src={file.url}
              title={file.name}
              className="w-full h-full rounded-xl bg-white"
            />
          )}

          {kind === "word" &&
            (showSyncfusionPreview ? (
              <div className="w-full h-full bg-white rounded-xl overflow-hidden">
                {docxLoading ? (
                  docLoadingState
                ) : (
                  <Suspense fallback={docLoadingState}>
                    <WordEditor
                      key={file.url}
                      initialContent={docxSfdt}
                      readOnly
                      stripHighlights
                      height="100%"
                      placeholders={[]}
                      setPlaceholders={noopSetPlaceholders}
                    />
                  </Suspense>
                )}
              </div>
            ) : (
              // The in-app converter could not read it (or is not set up): the
              // Office online viewer renders a Word file straight from its
              // signed link, the same way spreadsheets are shown.
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`}
                title={file.name}
                className="h-full w-full rounded-xl bg-white"
              />
            ))}

          {kind === "excel" && (
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`}
              title={file.name}
              className="w-full h-full rounded-xl bg-white"
            />
          )}

          {kind === "text" && (
            <iframe
              src={file.url}
              title={file.name}
              className="w-full h-full rounded-xl bg-white"
            />
          )}

          {kind === "audio" && (
            <div className="flex w-full max-w-xl flex-col items-center gap-5 rounded-2xl bg-white/[0.05] px-6 py-8 ring-1 ring-white/10">
              <span
                className={`grid h-20 w-20 place-items-center rounded-full bg-white/[0.08] ${KIND_ICON.audio}`}
              >
                {createElement(fileIconFor(file), { size: 32 })}
              </span>
              <p className="max-w-full truncate text-sm font-medium text-white/85">
                {file.name}
              </p>
              <audio
                src={file.url}
                controls
                className="w-full"
                style={{ colorScheme: "dark" }}
              />
            </div>
          )}

          {kind === "other" && (
            <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-2xl bg-white/[0.05] px-6 py-8 text-center ring-1 ring-white/10">
              <span
                className={`grid h-16 w-16 place-items-center rounded-2xl bg-white/[0.08] ${KIND_ICON.other}`}
              >
                {createElement(fileIconFor(file), { size: 28 })}
              </span>
              <div className="min-w-0 max-w-full">
                <p
                  className="truncate text-sm font-semibold text-white"
                  title={file.name}
                >
                  {file.name}
                </p>
                <p className="mt-1 text-xs text-white/55">
                  {label(
                    "notAvailable",
                    "Preview not available for this file type",
                  )}
                </p>
              </div>
              {downloadLink}
            </div>
          )}

          {hasMany && (
            <button
              type="button"
              onClick={next}
              title={label("next", "Next file (→)")}
              aria-label={label("next", "Next file (→)")}
              className={`${navButton} right-2 sm:right-4`}
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>

        {/* FILE STRIP — every file of the set: photos as thumbnails, the rest
            as tiles with their type icon, extension and name. The open one
            is ringed in the accent and kept in view. */}
        {hasMany && (
          <div className="dark border-t border-white/10 bg-white/[0.03] px-3 py-2.5">
            <div className="mx-auto flex w-fit max-w-full gap-2 overflow-x-auto pb-1">
              {state.files.map((f, i) => {
                const thumbKind = previewKind(f.name, f.type);
                const active = i === state.index;
                const tileExt = extensionOf(f.name);
                const openLabel = t("fileGallery.preview.open", {
                  defaultValue: "Open {{name}}",
                  name: f.name,
                });
                return (
                  <button
                    type="button"
                    key={f.id ?? `${f.url}-${i}`}
                    ref={active ? activeTileRef : undefined}
                    onClick={() => setIndex(i)}
                    title={f.name}
                    aria-label={openLabel}
                    aria-current={active ? "true" : undefined}
                    className={`relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl border-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
                      active
                        ? "border-white/90"
                        : "border-transparent opacity-55 hover:opacity-100"
                    }`}
                  >
                    {thumbKind === "image" ? (
                      <img
                        src={f.url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full bg-white/[0.07] object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-white/[0.07] px-1.5 text-white">
                        <span className="flex items-center gap-1">
                          {createElement(fileIconFor(f), {
                            size: 15,
                            className: KIND_ICON[thumbKind],
                          })}
                          {tileExt && (
                            <span className="text-[11px] font-bold uppercase tracking-wide text-white/85">
                              {tileExt}
                            </span>
                          )}
                        </span>
                        <span className="w-full truncate text-center text-[11px] text-white/55">
                          {f.name}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
